import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Typography,
  DialogTitle,
  Dialog,
  DialogContent,
  IconButton,
} from '@material-ui/core/';
import CloseIcon from '@material-ui/icons/Close';
import { convertSpacingToCss } from '../../helpers/stylesHelpers';

const useStyles = makeStyles({
  paperWidthMd: {
    maxWidth: 400,
  },
  paperWidthLg: {
    maxWidth: 800,
  },
  paperWidthXl: {
    maxWidth: 1000,
  },
  paper: {
    borderRadius: 17,
    padding: convertSpacingToCss('50px xl xl xl'),
  },
  closeButton: {
    position: 'absolute',
    top: 15,
    right: 15,
  },
  imgContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    marginBottom: convertSpacingToCss('lg'),
  },
  topIcon: {
    width: props => props.topIconWidth || 140,
  },
  dialogTitle: {
    padding: 0,
  },
  item: {
    marginBottom: convertSpacingToCss('lg'),
  },
  content: {
    padding: 0,
    overflowY: 'unset',
  },
});

const BaseDialog = props => {
  const classes = useStyles(props);
  const {
    isOpen,
    handleClose,
    title,
    description,
    subtitle,
    children,
    topIcon,
    topIconWidth,
    hideCloseButton,
    size,
  } = props;

  const align = size === 'xl' || size === 'lg' ? 'left' : 'center';

  const closeDialogHandler = () => {
    if (handleClose) {
      handleClose();
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={closeDialogHandler}
      classes={{
        paper: classes.paper,
        paperWidthMd: classes.paperWidthMd,
        paperWidthLg: classes.paperWidthLg,
        paperWidthXl: classes.paperWidthXl,
      }}
      maxWidth={size}
      fullWidth
    >
      <DialogTitle classes={{ root: classes.dialogTitle }} disableTypography>
        {!hideCloseButton && (
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            data-cy="baseDialogCloseButton"
            onClick={closeDialogHandler}
          >
            <CloseIcon />
          </IconButton>
        )}
        {topIcon && (
          <div className={classes.imgContainer}>
            <img
              src={topIcon}
              width={topIconWidth}
              className={classes.topIcon}
              alt="Top"
            />
          </div>
        )}
        {title && (
          <Typography
            variant="h5"
            color="textPrimary"
            align={align}
            component="div"
            className={classes.item}
          >
            <Box fontWeight="fontWeightBold">{title}</Box>
          </Typography>
        )}
        {description && (
          <Typography
            variant="body1"
            color="textPrimary"
            align={align}
            component="div"
            className={classes.item}
          >
            {description}
          </Typography>
        )}
        {subtitle && (
          <Typography
            variant="h6"
            color="textPrimary"
            align={align}
            component="div"
            className={classes.item}
          >
            <Box fontWeight="fontWeightBold">{subtitle}</Box>
          </Typography>
        )}
      </DialogTitle>
      <DialogContent classes={{ root: classes.content }}>
        {children}
      </DialogContent>
    </Dialog>
  );
};

BaseDialog.defaultProps = {
  topIcon: null,
  topIconWidth: null,
  hideCloseButton: false,
  size: 'md',
  title: null,
  subtitle: null,
  description: null,
};

BaseDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  description: PropTypes.string,
  children: PropTypes.objectOf(Object).isRequired,
  topIcon: PropTypes.string,
  topIconWidth: PropTypes.number,
  hideCloseButton: PropTypes.bool,
  // eslint-disable-next-line react/no-unused-prop-types
  size: PropTypes.oneOf(['md', 'lg', 'xl']),
};

export default BaseDialog;
